<template>
    <aside
        class="EvieSidebar"
        :class="{
            'EvieSidebar--mobileOpen': isMobileOpen,
            'EvieSidebar--mobileAnimating': isMobileAnimating,
        }"
    >
        <div class="EvieSidebar__header">
            <EidIcon class="EvieSidebar__headerIcon" :icon-src="faSparkles" />
            Evie AI
        </div>
        <NewButton
            class="EvieSidebar__newChat"
            variant="gradient"
            color="primary"
            size="sm"
            type="button"
            @click="$emit('new-chat')"
        >
            <EidIcon :icon-src="faPlus" />
            New Chat
        </NewButton>
        <div class="EvieSidebar__recents">
            Recents
        </div>
        <transition-group name="EvieSidebar__item" tag="div" class="EvieSidebar__history">
            <EvieSidebarConversationItem
                v-for="summary in summaries"
                :key="summary.conversationId"
                :summary="summary"
                :is-active="summary.conversationId === activeConversationId"
                :is-streaming="streamingConversationIds.includes(summary.conversationId)"
                :rp-name="rpName"
                @click="$emit('select-conversation', summary.conversationId)"
            />
        </transition-group>
    </aside>
</template>

<style lang="scss">
.EvieSidebar {
    display: flex;
    flex-direction: column;
    width: 260px;
    min-width: 260px;
    height: 100%;
    border-right: 1px solid var(--eidSecureBlue-10);
    background: var(--eidTprmBg);
    padding: 16px 8px;
    gap: 8px;

    &__header {
        display: flex;
        align-items: center;
        gap: 8px;
        font-family: var(--evie-font-stack);
        font-size: 16px;
        font-weight: 600;
        color: var(--eidSecureBlue);
        padding: 0 8px 4px;
    }

    &__headerIcon {
        font-size: 18px;
        flex-shrink: 0;
    }

    &__newChat {
        width: 100%;
    }

    &__recents {
        font-family: var(--evie-font-stack);
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--eidSecureBlue-50);
        padding: 4px 8px 0;
    }

    &__history {
        display: flex;
        flex-direction: column;
        gap: 2px;
        overflow-y: auto;
    }

    &__item-enter-active {
        transition: opacity 0.25s ease;
    }

    // PRODUCT-26110 Task 12b (TRANSITION_CLASSES): Vue 3 renamed the v2 `X-enter` class
    // to `X-enter-from` (still combined with `X-enter-active` during the enter phase).
    &__item-enter-from {
        opacity: 0;
    }

    @media (max-width: 1024px) {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 500;
        height: 100%;
        transform: translateX(-100%);

        &--mobileAnimating {
            transition: transform 0.25s ease;
        }

        &--mobileOpen {
            transform: translateX(0);
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faPlus } from '@fortawesome/pro-light-svg-icons';
    import { faSparkles } from '@fortawesome/pro-regular-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { EvieConversationSummary } from '@evidentid/tprm-portal-lib/models/evie/EvieConversationSummary.model';

    import NewButton from '@/components/new-button/NewButton.vue';

    import EvieSidebarConversationItem from './sub-components/EvieSidebarConversationItem.vue';

    export default defineComponent({
        name: 'EvieSidebar',
        components: { NewButton, EidIcon, EvieSidebarConversationItem },
        props: {
            conversationSummaries: {
                type: Object as PropType<Record<string, EvieConversationSummary>>,
                required: true,
            },
            activeConversationId: { type: String as PropType<string | null>, default: null },
            streamingConversationIds: { type: Array as PropType<string[]>, default: (): string[] => [] },
            isMobileOpen: { type: Boolean, default: false },
            isMobileAnimating: { type: Boolean, default: false },
            rpName: { type: String, required: true },
        },
        emits: [ 'new-chat', 'select-conversation' ],
        data() {
            return { faPlus, faSparkles };
        },
        computed: {
            summaries(): EvieConversationSummary[] {
                return Object.values(this.conversationSummaries);
            },
        },
    });
</script>
